/**
 * Data side of the customer picker (components/CustomerPicker.jsx).
 *
 * The Discounts screen used to fill a plain <select> from
 * GET /api/admin/customers. That endpoint is paginated, so the dropdown only
 * ever held its first page — 20 customers, alphabetical — and every other
 * customer of the store was impossible to choose.
 *
 * The picker instead asks GET /api/admin/customers/lookup for one page at a
 * time, searched by name or phone on the server, and appends further pages
 * as the admin scrolls. Nothing is ever loaded in bulk, and the store is
 * decided by the server from the login — never by anything sent from here.
 */
export const PICKER_PAGE_SIZE = 20;

export const lookupUrl = (query, page, pageSize = PICKER_PAGE_SIZE) => {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  const q = (query || "").trim();
  if (q) params.set("q", q);
  return `/api/admin/customers/lookup?${params.toString()}`;
};

export const emptyResults = (query = "") => ({ query, items: [], page: 0, total: 0, totalPages: 1 });

/** Results after one more page arrived: page 1 replaces the list, later
 *  pages are appended (a customer already listed is never repeated). */
export function mergePage(results, query, data) {
  const incoming = data?.customers || [];
  const page = data?.page || 1;
  const base = page <= 1 || results.query !== query ? [] : results.items;
  const seen = new Set(base.map((c) => c.id));
  return {
    query,
    items: [...base, ...incoming.filter((c) => !seen.has(c.id))],
    page,
    total: data?.total ?? incoming.length,
    totalPages: data?.total_pages || 1,
  };
}

export const hasMore = (results) => results.page >= 1 && results.page < results.totalPages;

export const customerLabel = (c) => (c ? [c.name || "—", c.phone].filter(Boolean).join(" · ") : "");

/**
 * Keeps the list for one open picker. `get(url)` is the API call (axios
 * shaped: resolves to `{data}`). `search` and `more` resolve to the new
 * results, or to `null` when a newer request has since been started — so a
 * slow answer to an old keystroke can never overwrite a newer one.
 */
export function createCustomerLookup(get) {
  let latest = 0;
  let results = emptyResults();
  const run = async (query, page, base) => {
    const mine = ++latest;
    const { data } = await get(lookupUrl(query, page));
    if (mine !== latest) return null;
    results = mergePage(base, query, data);
    return results;
  };
  return {
    get results() { return results; },
    search: (query) => run((query || "").trim(), 1, emptyResults()),
    more: () => (hasMore(results) ? run(results.query, results.page + 1, results) : Promise.resolve(results)),
  };
}
